import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { YStack, XStack, View, Text, Spinner, ScrollView } from "tamagui";

import SkinV2Screen from "@components/layout/SkinV2Screen";
import { v2 } from "@screen/Home/pages/GasShop/skin";
import { OrderCardV2 } from "@screen/Home/pages/OrderList/components/OrderCardV2";
import { SkinTabs } from "@screen/Home/pages/GasShop/skin";
import { toOrderUIStatus } from "@screen/Home/pages/OrderList/components/OrderStatusBadge";
import { colors } from "@styles/colors";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { buildXContextHeader } from "@utils/api/headers";
import { IdentityContext } from "@utils/store/user/useUser";

import InfoNote from "./components/ui/InfoNote";
import { listAccountOrderHistory, type OrderHistoryItem } from "./API/handleAccountHistoryAPI";
import { isOrderAmountHidden } from "@utils/func/orderAmountVisibility";

/** ISO → "MM/DD HH:mm"（本地時間） */
const fmt = (iso: string): string => {
  const d = new Date(iso);
  const pad = (x: number): string => String(x).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const summaryText = (items: { pn: string; qty: number }[]): string =>
  items.map((s) => `${s.pn} ×${s.qty}`).join("、");

/**
 * 瓦斯帳戶訂單紀錄頁。
 * 透過 X-Context（由 router state 的 IdentityContext 或 URL 的 contextId 組出）
 * 呼叫 GET /app-api/platform-orders/account-history，分頁 cursor 載入更多。
 */
export const AccountOrderHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { contextId } = useParams();
  const { cusJwtToken } = useCusAuth();

  const ctx = (location.state as { context?: IdentityContext } | null)?.context ?? null;
  const xContext = buildXContextHeader(ctx) ?? (contextId ? `context:${contextId}` : "");

  const [items, setItems] = useState<OrderHistoryItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [statusTab, setStatusTab] = useState<string>("all");

  useEffect(() => {
    if (!cusJwtToken || !xContext) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    listAccountOrderHistory({ jwtToken: cusJwtToken, xContext, params: { limit: 20 } })
      .then((res) => {
        if (!active) return;
        if (res.state === "success") {
          setItems(res.data.items);
          setTotalCount(res.data.total_count);
          setCursor(res.data.next_cursor);
          setHasMore(res.data.has_more);
        } else {
          console.log("account order history err", res.error);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cusJwtToken, xContext]);

  const loadMore = () => {
    if (!cusJwtToken || !xContext || !cursor || loadingMore) return;
    setLoadingMore(true);
    listAccountOrderHistory({ jwtToken: cusJwtToken, xContext, params: { limit: 20, cursor } })
      .then((res) => {
        if (res.state === "success") {
          setItems((prev) => [...prev, ...res.data.items]);
          setCursor(res.data.next_cursor);
          setHasMore(res.data.has_more);
        } else {
          console.log("account order history (more) err", res.error);
        }
      })
      .finally(() => setLoadingMore(false));
  };

  const back = () => navigate(`/account/detail/${contextId}`, { state: { context: ctx } });

  const vendorName = ctx?.primary_supplier?.supplier_name ?? "瓦斯行";

  /* 標題由釘住的標題列負責；藍色帶上留「哪一家 · 共幾筆」這行脈絡。 */
  /**
   * 狀態分頁籤：與訂單列表同一組（見 OrderList/index.tsx 的 filters）。
   * 在已載入的清單上做前端過濾，不重打 API——這頁是「此帳戶的歷史」，一次帶 20 筆、
   * 捲到底才續抓，切分頁籤時重打會把已經抓過的頁丟掉。
   */
  const STATUS_TABS = [
    { key: "all", label: "全部" },
    { key: "pending", label: "待確認" },
    { key: "waiting_for_pickup", label: "待接單" },
    { key: "delivering", label: "配送中" },
    { key: "completed", label: "已完成" },
    { key: "canceled", label: "已取消" },
  ];
  const shown = statusTab === "all" ? items : items.filter((o) => toOrderUIStatus(o.status) === statusTab);

  const subHeader = (
    <XStack ai="center" gap="$2" fw="wrap" mb={16}>
      <Text color={v2.hazeSub} fontSize={13}>
        {vendorName}
      </Text>
      <Text color={v2.disabled} fontSize={13}>
        ·
      </Text>
      <Text color={v2.hazeSub} fontSize={13}>
        共 {totalCount} 筆
      </Text>
    </XStack>
  );

  /* 分頁籤跟著 subHeader 坐在淡霧頁首上（tone="ink"），與訂單列表一致 */
  const tabs = (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <XStack pr="$8" pb={2}>
        <SkinTabs tone="ink" value={statusTab} onChange={setStatusTab} tabs={STATUS_TABS} />
      </XStack>
    </ScrollView>
  );

  return (
    <SkinV2Screen title="訂單紀錄" onBack={back} subHeader={subHeader}>
      <YStack className="gas-stagger-kids" gap="$3.5">
        {tabs}
        {loading ? (
          <YStack f={1} ai="center" jc="center" py="$8">
            <Spinner size="large" color={colors.primary} />
          </YStack>
        ) : shown.length === 0 ? (
          <YStack f={1} ai="center" jc="center" py="$8">
            <Text fontSize={14} color={v2.body}>
              {statusTab === "all" ? "目前沒有訂單紀錄" : "沒有符合的訂單"}
            </Text>
          </YStack>
        ) : (
          <>
            {shown.map((o) => (
              <OrderCardV2
                key={o.platform_order_id}
                o={o}
                onPress={() =>
                  navigate(`/orders/${o.platform_order_id}`, {
                    // 帶入此帳戶的瓦斯行，詳情頁標頭才不依賴目前已選身分
                    state: { xContext, supplierName: ctx?.primary_supplier?.supplier_name },
                  })
                }
              />
            ))}

            {hasMore && (
              <XStack
                ai="center"
                jc="center"
                gap="$2"
                bg={v2.card}
                br={999}
                py="$3"
                cursor={loadingMore ? "default" : "pointer"}
                pressStyle={{ opacity: 0.7 }}
                style={{ boxShadow: "0 2px 10px -4px rgba(21, 101, 192, 0.10)" }}
                onPress={loadMore}>
                {loadingMore ? (
                  <Spinner size="small" color={colors.primary} />
                ) : (
                  <Text fontSize={13} fontWeight="700" color={v2.primary}>
                    載入更多
                  </Text>
                )}
              </XStack>
            )}
          </>
        )}

        <InfoNote tone="primary">此處顯示此瓦斯帳戶的訂單歷史。</InfoNote>
      </YStack>
    </SkinV2Screen>
  );
};

export default AccountOrderHistory;
